import React from 'react';
import PropTypes from 'prop-types';
import { compose, withHandlers } from 'recompose';
import { encode } from 'querystring';
import StarRating from 'components/review/StarRating/StarRating';

import {
	UniversalSearchFilterContent,
	PriceRangeSlider,
	Toggle,
} from '@sparefoot/react-core';
import { TrackingClient } from '@sparefoot/tracking-client';

import {
	storageSizeOptions,
	vehicleSizeOptions,
	storageAmenities,
	vehicleAccommodations,
	vehicleRequirements,
} from 'config/units';
import { getFilterValueForAmenity } from 'utils/search/parseAmenityFromPath';

import './SearchFiltersVariation.scss';

// Checkmark SVG for checkbox filters
const CheckmarkSVG = (
	<svg
		width="12"
		height="10"
		viewBox="0 0 12 10"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M1 5L4.5 8.5L11 1"
			stroke="white"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

/** Query param keys to clear when switching TO standard storage */
const vehicleFilterParamKeys = [
	'vehicleAccommodation',
	'minLength',
	'maxLength',
	...vehicleRequirements.map((opt) => opt.value),
];

/** Query param keys to clear when switching TO vehicle storage */
const storageFilterParamKeys = ['sqft', 'onlyLocker', 'onlyWine'];

/**
 * Recompose HOC that creates filter handler functions from props.
 * Mirrors reactfoot's SearchFilters enhance pattern, scoped to
 * price + deals filters only for now.
 */
export const enhance = compose(
	withHandlers({
		/**
		 * Handles price range slider changes.
		 * Clamps to [0, 500] and strips boundary values to keep
		 * query params clean (undefined = no constraint).
		 */
		handlePriceRangeChange:
			(props) =>
			({ minPrice, maxPrice }) => {
				const newParams = {
					...props.queryParams,
					minPrice: minPrice > 0 ? minPrice : undefined,
					maxPrice: maxPrice && maxPrice < 500 ? maxPrice : undefined,
				};

				TrackingClient.trackSearchFiltered({
					search_filters: encode(props.queryParams),
					search_label: `price-${minPrice}-${maxPrice}`,
					search_term: 'price',
				});

				props.onFilter(newParams);
			},

		/**
		 * Toggles the "only show facilities with deals" filter.
		 * Sends undefined when toggled off to remove the param entirely.
		 */
		handleSpecialToggle: (props) => (value) => {
			const newParams = {
				...props.queryParams,
				hasSpecial: value ? value : undefined,
			};

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `special-${value}`,
				search_term: 'hasSpecial',
			});

			props.onFilter(newParams);
		},
		handleReviewSelect: (props) => (reviewOption) => {
			const newParams = {
				...props.queryParams,
				averageReview:
					reviewOption.value === '4' ? reviewOption.value : undefined,
			};

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `review-${reviewOption.value}`,
				search_term: 'averageReview',
			});

			props.onFilter(newParams);
		},

		/**
		 * Handles storage unit size selection.
		 * Sets sqft or onlyLocker param, clears vehicle length params.
		 */
		handleSizeSelect: (props) => (selOption) => {
			const newParams = { ...props.queryParams };
			const sizeOption = storageSizeOptions.find(
				(opt) => opt.value === selOption.value,
			);

			if (sizeOption) {
				if (sizeOption.isLocker) {
					newParams.onlyLocker = true;
					newParams.onlyWine = undefined;
					newParams.sqft = undefined;
				} else {
					newParams.sqft = sizeOption.value;
					newParams.onlyLocker = undefined;
				}
				// Clear all vehicle-specific params when selecting storage size
				vehicleFilterParamKeys.forEach((key) => {
					newParams[key] = undefined;
				});

				newParams.searchType = 'storage';
				newParams.type = 'storage';

				TrackingClient.trackSearchFiltered({
					search_filters: encode(props.queryParams),
					search_label: `sqft-${sizeOption.value}`,
					search_term: 'sqft',
				});
			}

			props.onFilter(newParams);
		},

		/**
		 * Handles vehicle unit size selection.
		 * Sets minLength/maxLength, clears storage sqft params.
		 */
		handleVehicleSizeSelect: (props) => (selOption) => {
			const newParams = { ...props.queryParams };
			const sizeOption = vehicleSizeOptions.find(
				(opt) => opt.value === selOption.value,
			);

			if (sizeOption) {
				newParams.minLength = sizeOption.value;
				newParams.maxLength = sizeOption.maxLength;
				// Clear storage-specific params when selecting vehicle size
				storageFilterParamKeys.forEach((key) => {
					newParams[key] = undefined;
				});
				newParams.searchType = 'vehicle';
				newParams.type = 'vehicle';

				TrackingClient.trackSearchFiltered({
					search_filters: encode(props.queryParams),
					search_label: `minLength-${sizeOption.value}`,
					search_term: 'minLength',
				});
			}

			props.onFilter(newParams);
		},

		/**
		 * Toggles a storage amenity checkbox.
		 * Sets the amenity param to true or undefined.
		 */
		handleAmenityToggle: (props) => (amenity, checked) => {
			const newParams = { ...props.queryParams };
			const amenityKey = amenity.value;

			if (amenityKey === 'onlyWine') {
				newParams.onlyLocker = undefined;
			}

			newParams[amenityKey] = checked ? true : undefined;

			// Clear the subPageType param when unchecking the filter
			// that corresponds to the current amenity landing page
			// context. This prevents the selector from re-injecting
			// the filter on the next render.
			if (
				!checked &&
				newParams.subPageType &&
				getFilterValueForAmenity(newParams.subPageType) === amenityKey
			) {
				newParams.subPageType = undefined;
			}

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `amenity-${amenityKey}-${checked}`,
				search_term: amenityKey,
			});

			props.onFilter(newParams);
		},

		/**
		 * Handles vehicle storage accommodation selection (radio behavior).
		 * Only one accommodation type can be selected at a time.
		 */
		handleVehicleStorageSelect: (props) => (option) => {
			const newParams = { ...props.queryParams };

			// If selecting the same option, deselect it
			if (props.queryParams.vehicleAccommodation === option.value) {
				newParams.vehicleAccommodation = undefined;
			} else {
				// Clear storage-specific params when selecting vehicle accommodation
				storageFilterParamKeys.forEach((key) => {
					newParams[key] = undefined;
				});
				newParams.vehicleAccommodation = option.value;
				newParams.searchType = 'vehicle';
				newParams.type = 'vehicle';
			}

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `vehicleAccommodation-${option.value}`,
				search_term: 'vehicleAccommodation',
			});

			props.onFilter(newParams);
		},

		/**
		 * Toggles a vehicle requirement checkbox.
		 * Sets the requirement param to true or undefined.
		 */
		handleVehicleRequirementToggle: (props) => (requirement, checked) => {
			const newParams = { ...props.queryParams };
			const reqKey = requirement.value;

			newParams[reqKey] = checked ? true : undefined;
			if (checked) {
				// Clear storage-specific params when selecting vehicle requirement
				storageFilterParamKeys.forEach((key) => {
					newParams[key] = undefined;
				});
				newParams.searchType = 'vehicle';
				newParams.type = 'vehicle';
			}

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `vehicleReq-${reqKey}-${checked}`,
				search_term: reqKey,
			});

			props.onFilter(newParams);
		},
	}),
);

/**
 * Search filters variation for SSDR.
 *
 * Implements full filter UI for search results pages:
 * - Row 1: Price + Ratings (side by side on desktop)
 * - Row 2 Col 1: Unit Sizes + Vehicle Unit Sizes
 * - Row 2 Col 2: Storage Amenities
 * - Row 2 Col 3: Vehicle Storage + Vehicle Requirements
 */
const SearchFiltersVariation = ({
	queryParams = {},
	onFilter,
	handlePriceRangeChange,
	handleSpecialToggle,
	handleReviewSelect,
	handleSizeSelect,
	handleVehicleSizeSelect,
	handleAmenityToggle,
	handleVehicleStorageSelect,
	handleVehicleRequirementToggle,
	facetCounts = {},
	idPrefix = '', // Unique prefix to avoid duplicate IDs when rendering multiple instances
}) => {
	// Generate unique IDs to avoid conflicts when both desktop and mobile trays are in DOM
	const toggleId = `${idPrefix}special-offers`;
	const reviewsId = `${idPrefix}reviews`;

	// Determine current storage size selection
	const selectedStorageSize = queryParams.onlyLocker
		? '0-5'
		: queryParams.sqft;

	const filterConfig = [
		// =========================================================
		// Row 1: Price + Ratings (column 0)
		// =========================================================
		{
			id: `${idPrefix}price`,
			type: 'custom',
			title: 'Price',
			column: '0',
			defaultOpen: true,
			component: (
				<>
					<PriceRangeSlider
						minPrice={queryParams?.minPrice || 0}
						maxPrice={queryParams?.maxPrice || 500}
						maxThumbFloor={10}
						onPriceChange={handlePriceRangeChange}
						maxPlaceHolder="Max Price"
						onTrack={(data) => {
							TrackingClient.trackSearchFiltered({
								search_label: `price-slider-${data?.minPrice}-${data?.maxPrice}`,
								search_term: 'price-slider',
							});
						}}
					/>
					<Toggle
						id={toggleId}
						label="Only Show Facilities With Deals"
						value={!!queryParams.hasSpecial}
						onChange={handleSpecialToggle}
						trackingLabel="special-offers-toggle"
						input={{
							value: !!queryParams.hasSpecial,
						}}
					/>
				</>
			),
		},
		{
			id: reviewsId,
			type: 'radio',
			title: 'Ratings',
			column: '0',
			defaultOpen: false,
			className: 'review-filter',
			options: [
				{ label: 'All Ratings', value: 'all' },
				{
					label: (
						<span className="four-star-reviews">
							<StarRating
								width={9}
								height={9}
								rating={4}
								fillColor="#146FB4"
								outOf={5}
							/>
							<span>& up</span>
						</span>
					),
					value: '4',
				},
			],
			selectedValue: queryParams.averageReview || 'all',
			onSelect: (option) => handleReviewSelect(option),
		},

		// =========================================================
		// Row 2 Column 1: Unit Sizes + Vehicle Unit Sizes
		// =========================================================
		{
			id: `${idPrefix}unit-sizes`,
			type: 'radio',
			title: 'Unit Sizes',
			column: '1',
			defaultOpen: false,
			options: storageSizeOptions.map((opt) => ({
				label: opt.label,
				value: opt.value,
			})),
			selectedValue: selectedStorageSize,
			onSelect: handleSizeSelect,
		},
		{
			id: `${idPrefix}vehicle-unit-sizes`,
			type: 'radio',
			title: 'Vehicle Unit Sizes',
			column: '1',
			defaultOpen: false,
			options: vehicleSizeOptions.map((opt) => ({
				label: opt.label,
				value: opt.value,
			})),
			selectedValue: queryParams.minLength,
			onSelect: handleVehicleSizeSelect,
		},

		// =========================================================
		// Row 2 Column 2: Storage Amenities
		// =========================================================
		{
			id: `${idPrefix}amenities`,
			type: 'checkbox',
			title: 'Storage Amenities',
			column: '2',
			defaultOpen: false,
			options: storageAmenities,
			checkBoxSVG: CheckmarkSVG,
			isSelected: (amenity) => !!queryParams[amenity.value],
			onToggle: handleAmenityToggle,
			isDisabled: (amenity) => {
				const facetKey = amenity.facetName || amenity.value;
				const count = facetCounts[facetKey];
				const isSelected = !!queryParams[amenity.value];
				return count === 0 && !isSelected;
			},
		},

		// =========================================================
		// Row 2 Column 3: Vehicle Storage + Vehicle Requirements
		// =========================================================
		{
			id: `${idPrefix}vehicle-storage`,
			type: 'radio',
			title: 'Vehicle Storage',
			column: '3',
			defaultOpen: false,
			options: vehicleAccommodations.map((opt) => ({
				label: opt.label,
				value: opt.value,
			})),
			selectedValue: queryParams.vehicleAccommodation,
			onSelect: handleVehicleStorageSelect,
			isDisabled: (option) => {
				const count = facetCounts[option.value];
				return (
					count === 0 &&
					queryParams.vehicleAccommodation !== option.value
				);
			},
		},
		{
			id: `${idPrefix}vehicle-requirements`,
			type: 'checkbox',
			title: 'Vehicle Requirements',
			column: '3',
			defaultOpen: false,
			options: vehicleRequirements,
			checkBoxSVG: CheckmarkSVG,
			isSelected: (req) => !!queryParams[req.value],
			onToggle: handleVehicleRequirementToggle,
			isDisabled: (req) => {
				const count = facetCounts[req.value];
				const isSelected = !!queryParams[req.value];
				return count === 0 && !isSelected;
			},
		},
	];

	return (
		<UniversalSearchFilterContent
			columns={4}
			queryParams={queryParams}
			filterConfig={filterConfig}
			onFilter={onFilter}
			className="ssdr-filters"
		/>
	);
};

SearchFiltersVariation.propTypes = {
	queryParams: PropTypes.object.isRequired,
	onFilter: PropTypes.func.isRequired,
	handlePriceRangeChange: PropTypes.func.isRequired,
	handleSpecialToggle: PropTypes.func.isRequired,
	handleReviewSelect: PropTypes.func.isRequired,
	handleSizeSelect: PropTypes.func.isRequired,
	handleVehicleSizeSelect: PropTypes.func.isRequired,
	handleAmenityToggle: PropTypes.func.isRequired,
	handleVehicleStorageSelect: PropTypes.func.isRequired,
	handleVehicleRequirementToggle: PropTypes.func.isRequired,
	facetCounts: PropTypes.object,
	idPrefix: PropTypes.string,
};

export default enhance(SearchFiltersVariation);
